import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, QueryParamsHandling } from '@angular/router';
import { EventTrackerInterface } from '@commons/tracker/event-tracker/event-tracker.interface';
import { EventTrackerService } from '@commons/tracker/event-tracker/event-tracker.service';
import { trackById, trackByIndex } from '@core/utils/utils';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { backButtonAnimation, listAnimation } from '@features/jobs/mini-list/mini-list.animation';
import { SearchRoutingEnum } from '@features/routing/search/search-routing.enum';
import { LocaleEnum } from '@models/commons/locales.enum';
import { Company } from '@models/company/company';
import { Job } from '@models/job/job';
import { Bookmark } from '@models/profile/bookmark';
import { JobSearchContextEnum } from '@models/search/job-search-context.enum';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import { CompanyRoutingFragmentEnum } from '@routing/company/company-routing.enum';
import { ProfileRoutingEnum } from '@routing/profile/profile-routing.enum';
import { ProfileState } from '@store/ngxs-profile/profile.state';
import { BookmarkToggle } from '@store/profile/bookmark/bookmark.actions';
import { BookmarkState } from '@store/profile/bookmark/bookmark.state';
import { JobSearchLoadMore } from '@store/search/job-search/job-search.actions';
import { JobSearchState } from '@store/search/job-search/job-search.state';
import { SessionState } from '@store/session/session.state';
import { combineLatest, merge, Observable, zip } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

@Component({
  templateUrl: './mini-list.component.html',
  styleUrls: ['./mini-list.component.scss'],
  animations: [backButtonAnimation, listAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MiniListComponent implements OnInit {
  @Select(SessionState.isLogged) isLogged$: Observable<boolean>;
  @Select(BookmarkState) bookmarkMap$: Observable<Map<string, Bookmark>>;

  jobId$: Observable<string>;
  context$: Observable<JobSearchContextEnum>;
  hits$: Observable<Job[]>;
  page$: Observable<number>;
  nbPages$: Observable<number>;
  queryId$: Observable<string>;
  loading$: Observable<boolean>;
  trackingDataMap$: Observable<Map<string, EventTrackerInterface>>;
  back$: Observable<{ routerLink: string[]; fragment?: string; queryParamsHandling?: QueryParamsHandling | null }>;
  company$: Observable<Company>;

  JobSearchContextEnum = JobSearchContextEnum;
  wizbiiDomain = environment.apiDomain;
  trackById = trackById;
  trackByIndex = trackByIndex;
  locale = environment.locale;
  LocaleEnum = LocaleEnum;

  constructor(route: ActivatedRoute, store: Store, private readonly eventTrackerService: EventTrackerService) {
    this.jobId$ = route.paramMap.pipe(
      map((paramMap) => paramMap.get('jobId')),
      distinctUntilChanged(),
      shareReplay(1)
    );

    const contextLogged$ = this.isLogged$.pipe(
      filter((isLogged) => isLogged),
      switchMap(() => route.queryParamMap),
      map((queryParamMap) =>
        Object.keys(JobSearchContextEnum).find((context) => queryParamMap.has(JobSearchContextEnum[context]))
      ),
      map((context) => JobSearchContextEnum[context] || JobSearchContextEnum.Default),
      shareReplay(1)
    );

    const contextUnlogged$ = this.isLogged$.pipe(
      filter((isLogged) => !isLogged),
      map(() => JobSearchContextEnum.Company),
      shareReplay(1)
    );

    this.context$ = merge(contextLogged$, contextUnlogged$).pipe(distinctUntilChanged(), shareReplay(1));

    this.hits$ = this.context$.pipe(switchMap((context) => store.select(JobSearchState.result(context, 'hits'))));
    this.page$ = this.context$.pipe(switchMap((context) => store.select(JobSearchState.result(context, 'page'))));
    this.nbPages$ = this.context$.pipe(switchMap((context) => store.select(JobSearchState.result(context, 'nbPages'))));
    this.queryId$ = this.context$.pipe(switchMap((context) => store.select(JobSearchState.result(context, 'queryId'))));
    this.loading$ = this.context$.pipe(switchMap((context) => store.select(JobSearchState.loading(context))));

    this.trackingDataMap$ = combineLatest([this.isLogged$, this.hits$, this.queryId$]).pipe(
      map(([isLogged, jobs = [], queryId]) =>
        jobs.reduce(
          (data, job, index) =>
            data.set(job.id, {
              eventCategory: 'Job Page',
              eventAction: 'Switch Job',
              eventLabel: isLogged ? 'Logged' : 'Unlogged',
              algolia: { type: 'click', queryId, position: index + 1, objectId: job.id },
            }),
          new Map<string, EventTrackerInterface>()
        )
      )
    );

    this.back$ = contextLogged$.pipe(
      distinctUntilChanged(),
      map((context) => {
        switch (context) {
          case JobSearchContextEnum.Application:
            return {
              routerLink: [
                '/',
                FeaturesRoutingEnum.Profile,
                store.selectSnapshot(ProfileState.authenticatedProfileId),
                ProfileRoutingEnum.Applications,
              ],
            };
          case JobSearchContextEnum.Bookmark:
            return {
              routerLink: [
                '/',
                FeaturesRoutingEnum.Profile,
                store.selectSnapshot(ProfileState.authenticatedProfileId),
                ProfileRoutingEnum.Bookmarks,
              ],
            };
          case JobSearchContextEnum.Company:
            return {
              routerLink: [
                '/',
                FeaturesRoutingEnum.Company,
                store.selectSnapshot(JobSearchState.filter(context)).company,
              ],
              fragment: CompanyRoutingFragmentEnum.Jobs,
            };
          case JobSearchContextEnum.Partner:
            return {
              routerLink: [
                '/',
                FeaturesRoutingEnum.Company,
                store.selectSnapshot(JobSearchState.filter(context)).partner,
              ],
              fragment: CompanyRoutingFragmentEnum.PartnersJobs,
            };
          default:
            return {
              routerLink: ['/', FeaturesRoutingEnum.Search, SearchRoutingEnum.Jobs],
              queryParamsHandling: 'preserve' as QueryParamsHandling,
            };
        }
      }),
      shareReplay(1)
    );

    this.company$ = this.isLogged$.pipe(
      filter((isLogged) => !isLogged),
      switchMap(() => this.hits$),
      filter((jobs) => jobs && jobs.length > 0),
      map((jobs) => jobs[0].company),
      shareReplay(1)
    );
  }

  ngOnInit(): void {
    combineLatest([this.isLogged$, this.hits$])
      .pipe(
        take(1),
        filter(([isLogged, jobs]) => !isLogged && jobs && jobs.length >= 2)
      )
      .subscribe(() => {
        this.eventTrackerService.send({
          eventCategory: 'Job Page',
          eventAction: 'Display List',
          eventLabel: 'Unlogged',
        });
      });
  }

  @Dispatch() toggleBookmark = (job: Job): Observable<BookmarkToggle> =>
    this.isLogged$.pipe(
      take(1),
      map((isLogged) => {
        this.eventTrackerService.send({
          eventCategory: 'Job Page',
          eventAction: 'Click Save Mini Liste',
          eventLabel: isLogged ? 'Logged' : 'Unlogged',
        });

        return new BookmarkToggle(job.id);
      })
    );

  @Dispatch() loadMore = (): any =>
    zip(this.context$, this.loading$, this.page$, this.nbPages$).pipe(
      map(([context, loading, page, ngPages]) => (!loading && page < ngPages ? new JobSearchLoadMore(context) : []))
    );
}
